/**
 * @file pages/well/WellHistoryPage.js
 * @description Well Butler — "History" (plan 06 §C.2 #7).
 *
 * Recent results produced ON THIS DEVICE. The backend persists every sizing /
 * diagnosis / audit row (tenant-scoped) but does NOT yet expose a list endpoint
 * (no GET /api/v1/well/history). So this reads the device-local recents log. When
 * the server list endpoint ships, swap loadHistory() for an apiGet('/v1/well/...')
 * call (cursor-paginated, per plan §C.2 #7). The screen is clearly labeled so the
 * tech knows it's this-device recents, not the full cloud history.
 */

/*****************************************************************
 *  TABLE OF CONTENTS                                             *
 *  SECTION 1 — Imports                                           *
 *  SECTION 2 — Each tool's name and icon (one list)              *
 *  SECTION 3 — Small helpers                                     *
 *  SECTION 4 — The History screen                                *
 *****************************************************************/

/*****************************************************************
 *  SECTION 1 — IMPORTS                                           *
 *****************************************************************/

import React, { useState } from 'react';
import { Card, Badge, Button } from '../../design-system';
import {
  loadHistory,
  countHiddenHistory,
  hideAllHistory,
  showHiddenHistory,
} from '../../lib/wellHistory';
import { bandFromConfidence, WellPage } from './wellShared';

/*****************************************************************
 *  SECTION 2 — EACH TOOL'S NAME AND ICON                         *
 *  One row per tool that writes to History. The empty-list      *
 *  message is built from this same list, so it never goes stale. *
 *****************************************************************/

// WHY (2026-10-07, James): defect #172 — Meg test results showed the raw word
// "megtest" and a dot, and Trench estimate / Summarize never wrote here. Every
// tool that writes History now has its own name and icon in this one list.
const HISTORY_TOOLS = {
  size: { label: 'Pump sizing', icon: '📏', askWords: 'size a pump' },
  megtest: { label: 'Meg test', icon: '⚡', askWords: 'run a meg test' },
  diagnose: { label: 'Diagnosis', icon: '🔎', askWords: 'run a diagnosis' },
  audit: { label: 'Photo audit', icon: '📷', askWords: 'audit a photo' },
  trench: { label: 'Trench estimate', icon: '⛏️', askWords: 'save a trench estimate' },
  summarize: { label: 'Report summary', icon: '📄', askWords: 'summarize a report' },
};

const UNKNOWN_TOOL = { label: 'Result', icon: '🕑' };   // older entries from a tool we no longer know

/*****************************************************************
 *  SECTION 3 — SMALL HELPERS                                     *
 *****************************************************************/

/* ---- toolOf(entry) ---------------------------------------------
   The name + icon for one history entry's tool.                   */
function toolOf(entry) {
  return HISTORY_TOOLS[entry.kind] || UNKNOWN_TOOL;
}

/* ---- emptyListMessage() ----------------------------------------
   "Nothing here yet. Size a pump, run a meg test, ... and it'll
   show up." — built from the tool list above.                     */
function emptyListMessage() {
  const everyAsk = Object.values(HISTORY_TOOLS).map((tool) => tool.askWords);
  const allButLast = everyAsk.slice(0, -1).join(', ');
  const lastAsk = everyAsk[everyAsk.length - 1];
  const sentence = `${allButLast}, or ${lastAsk}`;
  const capitalized = sentence.charAt(0).toUpperCase() + sentence.slice(1);
  return `Nothing here yet. ${capitalized} and it'll show up.`;
}

/* ---- timeAgo(isoTime) ------------------------------------------
   "just now", "5 min ago", "3 hr ago", or the date.               */
function timeAgo(isoTime) {
  const MINUTES_IN_AN_HOUR = 60;
  const HOURS_IN_A_DAY = 24;
  const MILLISECONDS_IN_A_MINUTE = 60000;
  try {
    const thenMilliseconds = new Date(isoTime).getTime();
    const minutesAgo = Math.round((Date.now() - thenMilliseconds) / MILLISECONDS_IN_A_MINUTE);
    if (minutesAgo < 1) {
      return 'just now';
    }
    if (minutesAgo < MINUTES_IN_AN_HOUR) {
      return `${minutesAgo} min ago`;
    }
    const hoursAgo = Math.round(minutesAgo / MINUTES_IN_AN_HOUR);
    if (hoursAgo < HOURS_IN_A_DAY) {
      return `${hoursAgo} hr ago`;
    }
    return new Date(isoTime).toLocaleDateString();
  } catch (_) {
    return '';
  }
}

/*****************************************************************
 *  SECTION 4 — THE HISTORY SCREEN                                *
 *  Reads/writes: this device's history list only.                *
 *  Never deletes: "Hide" stamps entries; "Show hidden" restores. *
 *****************************************************************/

export default function WellHistoryPage() {
  const [items, setItems] = useState(loadHistory());                 // shown results
  const [hiddenCount, setHiddenCount] = useState(countHiddenHistory()); // hidden results
  const [isAskingToHide, setIsAskingToHide] = useState(false);       // are-you-sure showing

  /* ---- refreshList() — re-read both counts after a change ---- */
  function refreshList() {
    setItems(loadHistory());
    setHiddenCount(countHiddenHistory());
  }

  /* ---- confirmHide() — the person said yes: hide, don't delete ---- */
  function confirmHide() {
    hideAllHistory();
    setIsAskingToHide(false);
    refreshList();
  }

  /* ---- bringBackHidden() — un-hide everything ---- */
  function bringBackHidden() {
    showHiddenHistory();
    refreshList();
  }

  return (
    <WellPage
      title="History"
      intro="Recent results from this device. Tap a tool again from Home to run a new one."
    >
      <Card variant="flat" padding="sm" style={{ marginBottom: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          This is a quick on-device list. Your full saved history across all your devices lands once
          the cloud history view is connected.
        </p>
      </Card>

      {items.length === 0 ? (
        <Card variant="outline" padding="md">
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{emptyListMessage()}</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {items.map((entry) => {
            const { tone, label } = bandFromConfidence(entry.confidence);
            const tool = toolOf(entry);
            return (
              <Card key={entry.id} variant="elevated" padding="md">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{ fontSize: '1.6rem' }} aria-hidden="true">{tool.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'var(--weight-semibold)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.title || tool.label}
                    </div>
                    <div style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>{entry.summary}</div>
                    <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {tool.label} · {timeAgo(entry.at)}
                    </div>
                  </div>
                  {entry.confidence && <Badge color={tone} variant="subtle" size="sm">{label}</Badge>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* WHY (2026-10-07, James): defect #172 — this used to wipe the list in one
          tap with no are-you-sure. Now it asks first, and it only HIDES. */}
      {items.length > 0 && !isAskingToHide && (
        <Button
          variant="ghost"
          fullWidth
          style={{ marginTop: 'var(--space-4)', minHeight: 44 }}
          onClick={() => setIsAskingToHide(true)}
        >
          Hide these results from the list
        </Button>
      )}

      {isAskingToHide && (
        <Card variant="outline" padding="md" style={{ marginTop: 'var(--space-4)' }}>
          <p style={{ margin: '0 0 var(--space-3)', lineHeight: 1.5 }}>
            Hide all {items.length} result{items.length === 1 ? '' : 's'} from this list?
            Nothing is deleted — you can bring them back any time with "Show hidden results".
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="primary" style={{ flex: 1, minHeight: 44 }} onClick={confirmHide}>
              Yes, hide them
            </Button>
            <Button variant="secondary" style={{ flex: 1, minHeight: 44 }} onClick={() => setIsAskingToHide(false)}>
              Keep them
            </Button>
          </div>
        </Card>
      )}

      {hiddenCount > 0 && (
        <Button
          variant="secondary"
          fullWidth
          style={{ marginTop: 'var(--space-3)', minHeight: 44 }}
          onClick={bringBackHidden}
        >
          Show hidden results ({hiddenCount})
        </Button>
      )}
    </WellPage>
  );
}
